import React, { useState } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { AlertPosition, AlertType, DisplayAlignment, DisplaySize } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import OptimizedMoleculeImg from '../../shared/component/molecule/OptimizedMoleculeImg';
import { StyledImgProps } from '../../shared/dto/StyleCompRepository';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { WorkViewerInfo } from '../../shared/component/headless/input/InputBody';
import { openNewTab } from '../../shared/util/openNewTab';
import { useValidation } from '../../shared/hooks/useValidation';
import { parseSupportedVideoUrl } from '../../shared/util/videoUrl';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import playIcon from '../../asset/icon/play.png';

interface WorkViewerVideoProps {
  title: string;
  convertedCloudFrontUrl: string;
  handleVideoUrlChange: (value: string) => void;
}


const WorkViewerVideo: React.FC<WorkViewerVideoProps> = ({ 
  title, 
  convertedCloudFrontUrl,
  handleVideoUrlChange
  }) => {
  const { isEditMode } = useEditMode();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const handleImageChange = useWorkViewStore((s) => s.updateImage);
  const handleImageFileChange = useWorkViewStore((s) => s.updateImageFile);
  const pendingImage = useWorkViewStore((s) => s.pendingImage);
  const { enqueueAlert } = useStandardAlertStore();
  const [isHovered, setIsHovered] = useState(false);
  const { isValidVideoUrl } = useValidation();

  const moveToVideoUrl = () => {
    if (!activeWork) return;
    if (!activeWork.videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(activeWork.videoUrl)) return;
    const videoUrl = parseSupportedVideoUrl(activeWork.videoUrl);
    if (!videoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: videoUrl.platform }),
      callBack: () => {
        openNewTab(videoUrl.url.href);
      }
    });
  }

  return (
    <>
    <WorkViewerVideoWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <ImageWrap>
      <OptimizedMoleculeImg
        srcUrl={convertedCloudFrontUrl}
        alt={title}
        displaySize={DisplaySize.REGULAR}
        displayAlignment={DisplayAlignment.CENTER}
        handleChange={handleImageChange}
        handleFileChange={handleImageFileChange}
        imageFile={pendingImage?.file}
        StyledImg={WorkImage as React.ComponentType<StyledImgProps>}
        priority={true}
      />
      {!isEditMode && (
        <PlayOverlay $isHovered={isHovered}>
          <UniversalIconBtn
            size="standard"
            border={false}
            backgroundImg={playIcon}
            onClick={moveToVideoUrl}
          />
        </PlayOverlay>
      )}
      </ImageWrap>

    {isEditMode && (
      <VideoInfoRow>
        <VideoInfoLabel>Video URL</VideoInfoLabel>
        <MoleculeInputDiv
          value={activeWork?.videoUrl || ""}
          defaultValue={""}
          DefaultDiv={DefaultDiv}
          placeholder={"YouTube or Vimeo URL only"}
          handleChange={(e) => handleVideoUrlChange(e.target.value)}
          inputStyle={WorkViewerInfo}
          StyledDiv={Info}
        />
      </VideoInfoRow>
    )}
    </WorkViewerVideoWrapper>
    </>
  );
}

const WorkViewerVideoWrapper = styled.div`
  position: relative;
  height: fit-content;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 1.5 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%; 
  object-fit: contain;
`;

const VideoInfoRow = styled.div`
  width: 100%;
  height: 18px;
  min-height: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  ${({ theme }) => theme.typography.Body_04};
`;
const VideoInfoLabel = styled.span`
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;
const Info = styled.div`
  flex: 1 1 auto;            /* 남은 공간 채움 */
  min-width: 0;              /* flex item 줄바꿈 허용 */
  text-align: left;          /* 읽기 쉽게 왼쪽 정렬 */
  white-space: normal;       /* 기본 줄바꿈 허용 */
  word-break: break-word;    /* 긴 단어 쪼개기 */
  overflow-wrap: anywhere;   /* 아주 긴 연속 문자열 강제 줄바꿈 */
  line-height: 1.2;          /* 여러 줄일 때 겹치지 않게 */
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

export default WorkViewerVideo;
